import React, { useEffect, useMemo, useState } from 'react';

import PortalHeader from '../../components/Portal/PortalHeader';
import {
	fetchPacksForAdmin,
	fetchPackEnrollments,
	fetchPackLeads,
} from '../../utils/packService';
import { csvEscapeCell } from '../../utils/csv';
import './WorkshopEnrollments.css';

// ── helpers ──
function tsToMs(ts) {
	if (!ts) return 0;
	if (typeof ts.toDate === 'function') return ts.toDate().getTime();
	if (ts.seconds) return ts.seconds * 1000;
	if (ts._seconds) return ts._seconds * 1000;
	return 0;
}
function fmtDate(ts) {
	const ms = tsToMs(ts);
	return ms
		? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		: '—';
}
const money = (v) => (v != null ? `$${v}` : '—');

// Maps to the .ps-badge--* modifiers in portalSurface.css. Replaced Bootstrap's
// `bg` variants on conversion; 'secondary' had no house equivalent and becomes
// 'neutral', which is the same visual role.
const STATUS_TONE = {
	confirmed: 'success',
	pending: 'warning',
	abandoned: 'neutral',
	refunded: 'danger',
};

function buildRows(enrollments, leads) {
	const rows = [];
	for (const e of enrollments) {
		rows.push({
			id: e.id,
			kind: 'enrollment',
			packId: e.packId,
			packTitle: e.packTitle,
			instrument: e.instrument,
			lessons: e.lessons,
			fullPerLessonValue: e.fullPerLessonValue,
			mmsCreditValue: e.mmsCreditValue,
			studentName: e.student?.name,
			studentAge: e.student?.age,
			parentName: e.parent?.name,
			parentEmail: e.parent?.email,
			parentPhone: e.parent?.phone,
			goals: e.options?.goals,
			interestedInFall: !!e.options?.interestedInFallLessons,
			amount: e.amountPaid,
			status: e.status === 'refunded' ? 'refunded' : 'confirmed',
			createdAt: e.createdAt,
			stripeRef: e.stripePaymentIntentId,
		});
	}
	for (const l of leads) {
		if (l.status === 'converted') continue; // appears as an enrollment instead
		rows.push({
			id: l.id,
			kind: 'lead',
			packId: l.packId,
			packTitle: l.packTitle,
			instrument: l.instrument,
			lessons: l.lessons,
			fullPerLessonValue: l.fullPerLessonValue,
			mmsCreditValue: l.mmsCreditValue,
			studentName: l.student?.name,
			studentAge: l.student?.age,
			parentName: l.parent?.name,
			parentEmail: l.parent?.email,
			parentPhone: l.parent?.phone,
			goals: l.options?.goals,
			interestedInFall: !!l.options?.interestedInFallLessons,
			amount: l.amount,
			status: l.status === 'pending_payment' ? 'pending' : 'abandoned',
			createdAt: l.createdAt,
			stripeRef: l.stripeSessionId,
		});
	}
	rows.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
	return rows;
}

function toCsv(rows) {
	const headers = [
		'Pack', 'Instrument', 'Lessons', 'Status', 'Student', 'Age', 'Parent', 'Email', 'Phone',
		'Amount Paid', 'Per-Lesson Value', 'MMS Credit to Issue', 'Goals/Notes', 'Fall Interest', 'Purchased',
	];
	const lines = [headers.join(',')];
	for (const r of rows) {
		lines.push(
			[
				r.packTitle, r.instrument, r.lessons, r.status, r.studentName, r.studentAge,
				r.parentName, r.parentEmail, r.parentPhone,
				r.amount != null ? `$${r.amount}` : '',
				r.fullPerLessonValue != null ? `$${r.fullPerLessonValue}` : '',
				r.mmsCreditValue != null ? `$${r.mmsCreditValue}` : '',
				r.goals, r.interestedInFall ? 'Yes' : 'No', fmtDate(r.createdAt),
			].map(csvEscapeCell).join(',')
		);
	}
	return lines.join('\n');
}

const LessonPackPurchases = () => {
	const [packs, setPacks] = useState([]);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	const [packFilter, setPackFilter] = useState('all');
	const [statusFilter, setStatusFilter] = useState('all');
	const [search, setSearch] = useState('');
	const [expandedId, setExpandedId] = useState(null);

	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const [ps, enrollments, leads] = await Promise.all([
					fetchPacksForAdmin(),
					fetchPackEnrollments(),
					fetchPackLeads(),
				]);
				if (!active) return;
				setPacks(ps);
				setRows(buildRows(enrollments, leads));
			} catch (err) {
				console.error('Failed to load pack purchases:', err);
				if (active) setError('Could not load pack purchases.');
			} finally {
				if (active) setLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	const filteredRows = useMemo(() => {
		const term = search.trim().toLowerCase();
		return rows.filter((r) => {
			if (packFilter !== 'all' && r.packId !== packFilter) return false;
			if (statusFilter !== 'all' && r.status !== statusFilter) return false;
			if (term) {
				const hay = `${r.studentName} ${r.parentName} ${r.parentEmail}`.toLowerCase();
				if (!hay.includes(term)) return false;
			}
			return true;
		});
	}, [rows, packFilter, statusFilter, search]);

	const handleExport = () => {
		const csv = toCsv(filteredRows);
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		const label =
			packFilter === 'all'
				? 'all-packs'
				: packs.find((p) => p.id === packFilter)?.slug || packFilter;
		a.href = url;
		a.download = `lesson-pack-purchases-${label}.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	// Totals across confirmed purchases (the credit Anthony needs to issue in MMS).
	const confirmed = filteredRows.filter((r) => r.status === 'confirmed');
	const totalCredit = confirmed.reduce((sum, r) => sum + (r.mmsCreditValue || 0), 0);

	return (
		<>
			<PortalHeader
				title='Lesson Pack Purchases'
				subtitle='Website-native private-lesson pack sales — export to MMS as account credits.'
			/>
			<div className='ps-page workshop-enrollments'>
				{loading && <div className='ps-spinner' role='status' aria-label='Loading' />}

				{!loading && error && <p className='ps-alert ps-alert--danger'>{error}</p>}

				{!loading && !error && (
					<>
						{/* Filters */}
						<div className='ps-toolbar'>
							<div className='ps-field we-filter'>
								<label className='ps-label' htmlFor='lpp-pack'>Pack</label>
								<select
									id='lpp-pack'
									className='ps-select'
									value={packFilter}
									onChange={(e) => setPackFilter(e.target.value)}>
									<option value='all'>All packs</option>
									{packs.map((p) => (
										<option key={p.id} value={p.id}>{p.title}</option>
									))}
								</select>
							</div>
							<div className='ps-field we-filter'>
								<label className='ps-label' htmlFor='lpp-status'>Status</label>
								<select
									id='lpp-status'
									className='ps-select'
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}>
									<option value='all'>All statuses</option>
									<option value='confirmed'>Confirmed</option>
									<option value='pending'>Pending</option>
									<option value='abandoned'>Abandoned</option>
									<option value='refunded'>Refunded</option>
								</select>
							</div>
							<div className='ps-field we-filter we-filter--search'>
								<label className='ps-label' htmlFor='lpp-search'>Search</label>
								<input
									id='lpp-search'
									type='search'
									className='ps-input'
									placeholder='Student, parent, or email'
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</div>
							<div className='we-toolbar-actions'>
								<wa-button
									appearance='outlined'
									variant='brand'
									size='medium'
									onClick={handleExport}>
									Export CSV
								</wa-button>
							</div>
						</div>

						<p className='we-count'>
							{filteredRows.length} purchase{filteredRows.length === 1 ? '' : 's'}
							{` · ${confirmed.length} confirmed`}
							{totalCredit > 0 ? ` · $${totalCredit} MMS credit to issue` : ''}
						</p>

						{/* Roster */}
						<div className='ps-table-wrap'>
							<table className='ps-table we-table'>
								<thead>
									<tr>
										<th>Student</th>
										<th>Parent</th>
										<th>Contact</th>
										<th>Pack</th>
										<th>Lessons</th>
										<th>Paid</th>
										<th>MMS Credit</th>
										<th>Status</th>
										<th>Purchased</th>
									</tr>
								</thead>
								<tbody>
									{filteredRows.length === 0 && (
										<tr>
											<td colSpan={9}>
												<div className='ps-empty'>No pack purchases match these filters.</div>
											</td>
										</tr>
									)}
									{filteredRows.map((r) => (
										<React.Fragment key={r.id}>
											<tr
												className='we-row'
												onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
												<td>
													{r.studentName}
													{r.studentAge ? <div className='we-subtle'>age {r.studentAge}</div> : null}
												</td>
												<td>{r.parentName}</td>
												<td>
													<div>{r.parentEmail}</div>
													<div className='we-subtle'>{r.parentPhone}</div>
												</td>
												<td>
													{r.packTitle}
													{r.instrument ? <div className='we-subtle'>{r.instrument}</div> : null}
												</td>
												<td>{r.lessons}</td>
												<td>{money(r.amount)}</td>
												<td>{money(r.mmsCreditValue)}</td>
												<td>
													<span className={`ps-badge ps-badge--${STATUS_TONE[r.status] || 'neutral'}`}>
														{r.status}
													</span>
												</td>
												<td>{fmtDate(r.createdAt)}</td>
											</tr>
											{expandedId === r.id && (
												<tr className='we-detail-row'>
													<td colSpan={9}>
														<div className='we-detail'>
															<div>
																<strong>MMS credit:</strong>{' '}
																{money(r.mmsCreditValue)}
																{r.lessons && r.fullPerLessonValue
																	? ` (${r.lessons} × ${money(r.fullPerLessonValue)})`
																	: ''}
															</div>
															<div>
																<strong>Goals / notes:</strong> {r.goals || '—'}
															</div>
															<div>
																<strong>Interested in ongoing lessons:</strong>{' '}
																{r.interestedInFall ? 'Yes' : 'No'}
															</div>
															<div className='we-subtle'>
																{r.kind} · ref {r.stripeRef || '—'}
															</div>
														</div>
													</td>
												</tr>
											)}
										</React.Fragment>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default LessonPackPurchases;
